import { useContext, useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles, useTheme } from 'react-jss'
import CreateJs from 'preload-js'
import { externalPreload } from '@/utils/dom'
import { Context as GeneralContext } from '@/context'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import { Context as LoadingContext } from '@/context/loading'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import PageTitle from '@/components/PageTitle'
import Footer from '@/components/Footer'
import ListElement from '@/components/ListElement'
import Meta from '@/components/Meta'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const ArchiveCollections = ({
  cpt: cptFromProps,
  stub,
}) => {
  const { headerHeight, enableArchiveToScroll, setEnableArchiveToScroll } = useContext(GeneralContext)
  const { setReady } = useContext(LoadingContext)
  const { scroll } = useContext(ScrollbarContext)
  const classes = useStyles({ headerHeight })
  const preload = useRef(new CreateJs.LoadQueue())
  const [collections, setCollections] = useState([])
  const [load, setLoad] = useState(false)
  const $root = useRef()
  const theme = useTheme()

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, categories, page, scrollOffset } = useSelector((state) => ({
    current: state.page.current,
    categories: state.page.taxonomies[cptFromProps],
    strings: state.language.string,
    page: state.page.pages[stub],
    scrollOffset: state.page.archiveScrollOffset,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const getTaxonomiesList = useCallback((data) => dispatch(actions.page.getAllTerms(data)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const setArchiveScrollOffset = useCallback((num) => dispatch(actions.page.setArchiveScrollOffset(num)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Preload Main Images: preloading only thre three first image & imageHover
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    collections.slice(0, 3).map((item) => {
      preload.current.loadFile({ id: `img-${item.id}`, src: item.image })
      return false
    })
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    if (!page) getPage(stub)
    if (!categories) getTaxonomiesList('_cta_collection')
  }

  /*------------------------------
  If there are products & page is loaded from redux, complete the preload
  ------------------------------*/
  useEffect(() => {
    if (collections.length > 0 && page !== undefined) preloadMedia()
  }, [collections, page])

  /*------------------------------
  Map Categories to Product
  ------------------------------*/
  useEffect(() => {
    if (categories !== undefined) {
      const catMapped = categories.map((cat) => {
        const imgSizes = cat.meta.taxonomy_listing_image_id_sizes !== '' ? JSON.parse(cat.meta.taxonomy_listing_image_id_sizes) : ''
        return ({
          id: cat.id,
          title: cat.meta.section_title,
          slug: cat.slug,
          link: cat.link,
          image: imgSizes?.medium?.[0],
          year: cat.meta.year,
          collection: cat.name.replace('@', ''),
        })
      })
      setCollections(catMapped)
    }
  }, [categories])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
    return () => {
      if (scroll.current) setArchiveScrollOffset(scroll.current.y)
      setEnableArchiveToScroll(false)
    }
  }, [])

  /*------------------------------
  Trigger resize to update scroll
  ------------------------------*/
  useEffect(() => {
    if (load && !theme.detect.isIE11) window.dispatchEvent(new Event('resize'))
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render List
  ------------------------------*/
  const renderList = () => {
    return (
      <>
        <div className={classes.listIntro}>
          <PageTitle
            title={page.title.rendered}
            parallax
            delay={1}
          />
        </div>
        <div className={classes.list}>
          {collections.map((collection) => (
            <ListElement
              key={collection.id}
              item={collection}
              themeBtn="light"
            />
          ))}
        </div>
      </>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderList()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveCollections ${classes.root}`}
      ref={$root}
      scrollOffset={enableArchiveToScroll ? scrollOffset : 0}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default ArchiveCollections
